import React, { useEffect } from 'react'
import styled from '@emotion/styled'
import { fontSizeSmall, fontSize2XSmall, grayDark40, grayLight20, highlightBlueBase } from '../../constants/principles'
import Textbox from '../generic/floatingText'
import { FlexDiv } from '../generic/Basics'
import Warning from '../generic/Warn'
import Error from '../generic/Error'
import { StringUtils } from '../../utils/siteUtils'
import LargeCheckbox from '../generic/largeCheckbox'
import { BrowserUtils } from '../../utils/siteUtils'
import { getFromBrowserStorage } from '../../utils/storeUtils'

const ACCEPT_TERMS_TEXT = 'I accept the Program Terms and Conditions'

const CONTACT_FIELDS = [
  { label: 'First Name', id: 'firstName', type: 'text', error: 'first name', unformat: StringUtils.preventTypingNumber },
  { label: 'Last Name', id: 'lastName', type: 'text', error: 'last name', unformat: StringUtils.preventTypingNumber },
  { label: 'Email Address', id: 'email', type: 'email', error: 'email' },
  { label: 'Phone Number', id: 'phone', type: 'tel', error: 'phone number', maxLength: 14, format: StringUtils.formatPhoneString, unformat: StringUtils.phoneDigitsOnly },
  { label: 'ZIP Code', id: 'zip', type: 'text', error: '5-digit ZIP code', maxLength: 5, inputMode: 'numeric', pattern: '[0-9]*', unformat: e => e.replace(/[^\d]+/, '') }
]

const leaveAsIs = v => v

export default ({ zipInfo, experience, isCobrand, isAssigned, isAdmin, customerInfo, globalState, updateGlobalState, validateZip, validateUserInfo, isError, errors, setHasScrolled, scroll, isDesktop, offerInfo = {} }) => {
  CONTACT_FIELDS.at(-1).validate = validateZip

  const getFromCustomerOrGlobal = param => {
    if (param === 'zip' && !globalState.zip && globalState.useDealerZip && BrowserUtils.isBrowser && getFromBrowserStorage('ZipCode', false)?.ZipCode) {
      customerInfo[param] = getFromBrowserStorage('ZipCode', false)?.ZipCode
    }
    return globalState[param] || ''
  }

  if (!globalState.firstName) {
    if (customerInfo.firstName) {
      updateGlobalState(customerInfo)
    } else if (offerInfo.firstName) {
      updateGlobalState(offerInfo)
      //needed to fire the zip hook and validate zip and to prevent it from unfilling in edit
      validateZip(offerInfo.zip)
    }
  }
  const targetElement = isError && errors.length && `#${errors[0] + 'Error'}`
  useEffect(() => {
    if (isError && targetElement) {
      scroll(targetElement)
    } else {
      scroll('body', 'top')
    }
    setHasScrolled(true)
  }, [isError, targetElement])

  const kbbUrl = globalState.environment.kbb_Url
  const showMyKbb = experience.toLowerCase() === 'kbb' && !isCobrand && !isAssigned && !isAdmin

  const onTextBoxChange = ({ id, label, unformat, validate }) => newValue => {
    if (id !== 'zip' && unformat) newValue = unformat(newValue)
    validate ? validate(newValue) : validateUserInfo(id, newValue)

    trackContactInput(label)
  }

  const trackContactInput = label => updateGlobalState({ fireAnalytics: `contact_input:${label}/` })

  const { termsAccepted } = globalState
  const toggleTerms = () => {
    updateGlobalState({ termsAccepted: !termsAccepted })
    trackContactInput(ACCEPT_TERMS_TEXT)
  }

  return (
    <>
      <ContentDiv isDesktop={isDesktop}>
        <HeaderDiv data-testid={`ci-txt-where-send-ico`}>Where should we send your Instant Cash Offer?</HeaderDiv>
        <InfoDiv>
          {CONTACT_FIELDS.map((field, index) => {
            const { label, id, type, error, format, unformat, maxLength, inputMode, pattern } = field
            return (
              <BoxDiv key={index}>
                <Textbox
                  id={id}
                  testId={`ci-input-${id.toLowerCase()}`}
                  maskData={true}
                  label={label}
                  type={type}
                  formatMask={format || leaveAsIs}
                  unformatMask={unformat || leaveAsIs}
                  value={getFromCustomerOrGlobal(id)}
                  isError={globalState[`${id}Error`] || (isError && errors.includes(id))}
                  onChange={onTextBoxChange(field)}
                  maxLength={maxLength}
                  inputMode={inputMode}
                  pattern={pattern}
                  autoFocus={id === (isAdmin ? 'zip' : 'firstName')}
                />
                {(globalState[`${id}Error`] || (isError && errors.includes(id))) && <Error leftAlign={true} isError={isError || globalState[`${id}Error`]} id={`${id}Error`} text={`Please enter a valid ${error}.`} />}
              </BoxDiv>
            )
          })}
          {zipInfo.serviceableZipError && !zipInfo.zipError && (
            <Warning
              testId={`ci-warn-no-dealer`}
              title={'No Participating Dealers found in your area.'}
              text={"Unfortunately, there are no dealers in your area to redeem an Instant Cash Offer. However, you can still see what your car is worth by clicking 'Get My Offer'."}
              leftAlign={true}
            />
          )}
          {showMyKbb && (
            <CheckDiv>
              <LargeCheckbox id="myKbbCheckbox" isChecked={globalState.myKbbChecked || false} value={globalState.myKbbChecked} onChange={e => updateGlobalState({ myKbbChecked: !globalState.myKbbChecked, fireAnalytics: `contact_infor_createacct` })}>
                I’d like to create a MyKBB account to easily track my Instant Cash Offer and also to receive tips, tools and resources via email from Kelley Blue Book and affiliates.
              </LargeCheckbox>
            </CheckDiv>
          )}
          {!isAdmin && (
            <>
              <CheckDiv>
                <LargeCheckbox testId="ci-cb-accept-term" id="termsCheckbox" isChecked={!!termsAccepted} value={termsAccepted} onChange={toggleTerms}>
                  I accept the{' '}
                  <A target="_blank" href={`${kbbUrl}/company/terms-of-service/#termsAndConditions`}>
                    Program Terms and Conditions
                  </A>
                  .
                </LargeCheckbox>
              </CheckDiv>
              {isError && errors.includes('termsAccepted') && <Error leftAlign={true} isError={isError} id={'termsAcceptedError'} text={'Please review and accept the Program Terms and Conditions.'} margin={'0 0 8px 0'} />}
            </>
          )}
        </InfoDiv>
      </ContentDiv>
    </>
  )
}

const A = styled.a`
  text-decoration: none;
  color: ${highlightBlueBase};
  :hover {
    text-decoration: underline;
  }
`
const ContentDiv = styled(FlexDiv)`
  padding-top: 32px;
  padding-bottom: 32px;
  color: ${grayDark40};
  border-bottom: 1px solid ${grayLight20};
  width: ${p => (p.isDesktop ? 80 : 100)}%;
`
const HeaderDiv = styled.div`
  font-size: ${fontSizeSmall};
  text-align: center;
  font-weight: bold;
  width: 100%;
`
const InfoDiv = styled(FlexDiv)`
  padding-top: 32px;
  align-items: stretch;
  width: 300px;
`
const BoxDiv = styled.div`
  padding-bottom: 16px;
  text-align: left;
`
const CheckDiv = styled.div`
  font-size: ${fontSize2XSmall};
  padding: 8px 0 8px 22px;
  text-indent: -22px;
`
